import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, GripVertical, Plus, Trash2, Send, ChevronRight } from "lucide-react";
import ProviderDashboardLayout from "@/layouts/ProviderDashboardLayout";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const STEPS = [
  { key: "basics", label: "Basics" },
  { key: "pricing", label: "Pricing" },
  { key: "packages", label: "Packages" },
  { key: "addons", label: "Add-ons" },
  { key: "local", label: "Local details" },
  { key: "digital", label: "Digital details" },
  { key: "requirements", label: "Requirements" },
  { key: "availability", label: "Availability" },
  { key: "media", label: "Media" },
  { key: "faqs", label: "FAQs" },
  { key: "policies", label: "Policies" },
  { key: "seo", label: "SEO" },
  { key: "review", label: "Review" },
];

const SERVICE_TYPES = [
  "instant_fixed_local",
  "hourly_local",
  "quote_local_project",
  "digital_package",
  "custom_digital_project",
  "consultation",
  "recurring_service",
];
const PRICING_MODELS = [
  "fixed", "hourly", "starting_price", "price_range",
  "package_pricing", "quote_required", "free_consultation", "recurring",
];
const KINDS = ["local", "digital", "hybrid"];
const QUESTION_TYPES = [
  "written", "multiple_choice", "file_upload", "image_upload", "video_upload", "document_upload",
  "website_link", "brand_info", "address", "measurements", "reference_examples",
  "access_credentials", "agreement_checkbox",
];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const inputCSS = `
.wz-input { width:100%; border-radius:.75rem; border:1px solid #E2E8F0; background:#fff; padding:.65rem 1rem; font-size:.9rem; color:#071b22; outline:none; transition:border-color .15s, box-shadow .15s; }
.wz-input:focus { border-color:#165a71; box-shadow:0 0 0 3px rgba(22,90,113,0.15); }
`;

export default function ServiceWizardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [faqs, setFaqs] = useState([]);

  // Bootstrap: either load existing or wait for basics form
  useEffect(() => {
    (async () => {
      const cats = await api.get("/categories");
      setCategories(cats.data);
      if (id) {
        const list = await api.get("/services/mine/list");
        const found = list.data.find((x) => x.id === id);
        if (found) {
          // Fetch full doc (list returns public shape without every field)
          const { data } = await api.get(`/services/${found.slug || found.id}`);
          const merged = { ...found, ...data.service };
          setService(merged);
          setPackages(data.packages || []);
          setAddons(data.addons || []);
          setRequirements(data.requirements || []);
          setFaqs(data.faqs || []);
        }
      }
    })();
  }, [id]);

  const isDigital = service?.kind === "digital" || service?.kind === "hybrid";
  const isLocal = service?.kind === "local" || service?.kind === "hybrid";

  // Skip local/digital steps that don't apply to the picked kind
  const activeSteps = useMemo(() => {
    return STEPS.filter((s) => (s.key === "local" ? isLocal : s.key === "digital" ? isDigital : true));
  }, [isLocal, isDigital]);

  const currentStep = activeSteps[step] || STEPS[0];

  // ---- CRUD helpers ------------------------------------------------------

  const createDraft = async (payload) => {
    setCreating(true);
    try {
      const { data } = await api.post("/services", payload);
      setService(data);
      toast.success("Draft created — keep going.");
      navigate(`/dashboard/services/${data.id}/edit`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not create service");
    } finally {
      setCreating(false);
    }
  };

  const save = async (patch) => {
    if (!service) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/services/${service.id}`, patch);
      setService((s) => ({ ...s, ...data }));
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const next = () => setStep((s) => Math.min(s + 1, activeSteps.length - 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    try {
      const { data } = await api.post(`/services/${service.id}/submit`);
      setService((s) => ({ ...s, ...data }));
      toast.success(`Submitted — status: ${data.status || "pending_review"}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not submit");
    }
  };

  // Child helpers
  const reloadChildren = async () => {
    if (!service) return;
    const [p, a, r, f] = await Promise.all([
      api.get(`/services/${service.id}/packages`),
      api.get(`/services/${service.id}/addons`),
      api.get(`/services/${service.id}/requirements`),
      api.get(`/services/${service.id}/faqs`),
    ]);
    setPackages(p.data); setAddons(a.data); setRequirements(r.data); setFaqs(f.data);
  };

  return (
    <ProviderDashboardLayout>
      <div className="mb-6 flex items-center gap-2 text-xs text-[#252A34]/60">
        <button onClick={() => navigate("/dashboard/services")} className="hover:text-[#165a71] inline-flex items-center gap-1" data-testid="wizard-back-to-list">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to services
        </button>
      </div>

      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Service builder</div>
        <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]" data-testid="wizard-title">
          {service ? "Edit service" : "Create a new service"}
        </h1>
        {service && (
          <div className="mt-2 text-sm text-[#252A34]/70">Status: <b className="capitalize">{service.status?.replace(/_/g, " ")}</b>{saving && <span className="ml-2 text-[#165a71]">· saving…</span>}</div>
        )}
      </div>

      {/* Step nav */}
      {service && (
        <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-2 overflow-x-auto" data-testid="wizard-steps">
          {activeSteps.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              data-testid={`wizard-step-${s.key}`}
              className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                i === step ? "bg-[#071b22] text-white" : i < step ? "text-[#165a71] bg-[#165a71]/10" : "text-[#252A34]/60"
              }`}
            >
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
      )}

      <motion.div
        key={step}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8"
      >
        {!service ? (
          <BasicsStep categories={categories} onCreate={createDraft} creating={creating} />
        ) : currentStep.key === "basics" ? (
          <EditBasicsStep service={service} categories={categories} onSave={save} />
        ) : currentStep.key === "pricing" ? (
          <PricingStep service={service} onSave={save} />
        ) : currentStep.key === "packages" ? (
          <PackagesStep service={service} rows={packages} onChanged={reloadChildren} />
        ) : currentStep.key === "addons" ? (
          <AddonsStep service={service} rows={addons} onChanged={reloadChildren} />
        ) : currentStep.key === "local" ? (
          <LocalDetailsStep service={service} onSave={save} />
        ) : currentStep.key === "digital" ? (
          <DigitalDetailsStep service={service} onSave={save} />
        ) : currentStep.key === "requirements" ? (
          <RequirementsStep service={service} rows={requirements} onChanged={reloadChildren} />
        ) : currentStep.key === "availability" ? (
          <AvailabilityStep service={service} onSave={save} />
        ) : currentStep.key === "media" ? (
          <MediaStep service={service} onSave={save} />
        ) : currentStep.key === "faqs" ? (
          <FaqsStep service={service} rows={faqs} onChanged={reloadChildren} />
        ) : currentStep.key === "policies" ? (
          <PoliciesStep service={service} onSave={save} />
        ) : currentStep.key === "seo" ? (
          <SeoStep service={service} onSave={save} />
        ) : (
          <ReviewStep service={service} packages={packages} addons={addons} requirements={requirements} faqs={faqs} onSubmit={submit} />
        )}
      </motion.div>

      {service && (
        <div className="mt-6 flex items-center gap-3">
          <button onClick={back} disabled={step === 0} data-testid="wizard-prev" className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium disabled:opacity-40">
            Previous
          </button>
          <div className="flex-1" />
          {step < activeSteps.length - 1 ? (
            <button onClick={next} data-testid="wizard-next" className="rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#11485a] transition-colors inline-flex items-center gap-2">
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={submit} data-testid="wizard-submit" className="rounded-full bg-[#071b22] text-white px-5 py-2.5 text-sm font-medium hover:bg-black transition-colors inline-flex items-center gap-2">
              Submit for review <Send className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      <style>{inputCSS}</style>
    </ProviderDashboardLayout>
  );
}

// ==================== Step components ====================

function BasicsStep({ categories, onCreate, creating }) {
  const [title, setTitle] = useState("");
  const [category_id, setCategoryId] = useState("");
  const [service_type, setServiceType] = useState("instant_fixed_local");
  const [kind, setKind] = useState("local");
  const [short_description, setShort] = useState("");

  const roots = categories.filter((c) => c.depth === 0 && (kind === "hybrid" ? true : c.kind === kind));

  const submit = (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title is required");
    onCreate({ title, category_id: category_id || undefined, service_type, kind, short_description });
  };

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-5" data-testid="wizard-basics-form">
      <StepHeader title="Start with the basics" subtitle="You'll be able to change everything as you go. This just creates the draft." />
      <FieldRow>
        <F label="Title *">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="wz-input" data-testid="basics-title" placeholder="e.g. Deep house cleaning" />
        </F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Service kind">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="wz-input" data-testid="basics-kind">
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </F>
        <F label="Service type">
          <select value={service_type} onChange={(e) => setServiceType(e.target.value)} className="wz-input" data-testid="basics-service-type">
            {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </F>
      </FieldRow>
      <FieldRow>
        <F label="Category">
          <select value={category_id} onChange={(e) => setCategoryId(e.target.value)} className="wz-input" data-testid="basics-category">
            <option value="">Select a category…</option>
            {roots.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </F>
      </FieldRow>
      <FieldRow>
        <F label="Short description">
          <textarea rows={3} value={short_description} onChange={(e) => setShort(e.target.value)} className="wz-input" data-testid="basics-short-desc" placeholder="One-liner customers will see on cards." />
        </F>
      </FieldRow>
      <div className="pt-2">
        <button type="submit" disabled={creating} data-testid="basics-create-draft" className="rounded-full bg-[#165a71] text-white px-6 py-2.5 font-medium hover:bg-[#11485a] transition-colors disabled:opacity-60">
          {creating ? "Creating…" : "Create draft →"}
        </button>
      </div>
    </form>
  );
}

function EditBasicsStep({ service, categories, onSave }) {
  const [form, setForm] = useState({
    title: service.title || "",
    short_title: service.short_title || "",
    kind: service.kind || "local",
    service_type: service.service_type || "instant_fixed_local",
    category_id: service.category_id || "",
    subcategory_id: service.subcategory_id || "",
    short_description: service.short_description || "",
    full_description: service.full_description || "",
    search_tags: (service.search_tags || []).join(", "),
    languages: (service.languages || ["English"]).join(", "),
    featured_image: service.featured_image || "",
  });
  const roots = categories.filter((c) => c.depth === 0);
  const subs = categories.filter((c) => c.parent_id === form.category_id);

  const save = () => onSave({
    title: form.title,
    short_title: form.short_title || null,
    kind: form.kind,
    service_type: form.service_type,
    category_id: form.category_id || null,
    subcategory_id: form.subcategory_id || null,
    short_description: form.short_description || null,
    full_description: form.full_description || null,
    search_tags: form.search_tags.split(",").map((s) => s.trim()).filter(Boolean),
    languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
    featured_image: form.featured_image || null,
  });

  return (
    <div className="max-w-2xl space-y-5" data-testid="wizard-basics-edit">
      <StepHeader title="Basics" subtitle="Refine the title, category, and description customers see first." />
      <FieldRow><F label="Title"><input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="wz-input" data-testid="basics-edit-title" /></F></FieldRow>
      <FieldRow><F label="Short title (card / SEO fallback)"><input value={form.short_title} onChange={(e) => setForm({...form, short_title: e.target.value})} className="wz-input" data-testid="basics-edit-short-title" /></F></FieldRow>
      <FieldRow cols={2}>
        <F label="Kind"><select value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})} className="wz-input" data-testid="basics-edit-kind">{KINDS.map((k) => <option key={k}>{k}</option>)}</select></F>
        <F label="Service type"><select value={form.service_type} onChange={(e) => setForm({...form, service_type: e.target.value})} className="wz-input" data-testid="basics-edit-service-type">{SERVICE_TYPES.map((t) => <option key={t}>{t}</option>)}</select></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Category"><select value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value, subcategory_id: ""})} className="wz-input" data-testid="basics-edit-category"><option value="">—</option>{roots.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></F>
        <F label="Subcategory"><select value={form.subcategory_id} onChange={(e) => setForm({...form, subcategory_id: e.target.value})} className="wz-input" data-testid="basics-edit-subcategory" disabled={!form.category_id}><option value="">—</option>{subs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></F>
      </FieldRow>
      <FieldRow><F label="Short description"><textarea rows={2} value={form.short_description} onChange={(e) => setForm({...form, short_description: e.target.value})} className="wz-input" data-testid="basics-edit-short-desc" /></F></FieldRow>
      <FieldRow><F label="Full description"><textarea rows={6} value={form.full_description} onChange={(e) => setForm({...form, full_description: e.target.value})} className="wz-input" data-testid="basics-edit-full-desc" /></F></FieldRow>
      <FieldRow cols={2}>
        <F label="Search tags (comma-sep)"><input value={form.search_tags} onChange={(e) => setForm({...form, search_tags: e.target.value})} className="wz-input" data-testid="basics-edit-tags" /></F>
        <F label="Languages (comma-sep)"><input value={form.languages} onChange={(e) => setForm({...form, languages: e.target.value})} className="wz-input" data-testid="basics-edit-langs" /></F>
      </FieldRow>
      <FieldRow><F label="Featured image URL"><input value={form.featured_image} onChange={(e) => setForm({...form, featured_image: e.target.value})} className="wz-input" placeholder="https://…" data-testid="basics-edit-featured-image" /></F></FieldRow>
      <SaveButton onClick={save} />
    </div>
  );
}

function PricingStep({ service, onSave }) {
  const [f, setF] = useState({
    pricing_model: service.pricing_model || "fixed",
    base_price: service.base_price || 0,
    hourly_rate: service.hourly_rate || "",
    min_hours: service.min_hours || "",
    max_hours: service.max_hours || "",
    starting_price: service.starting_price || "",
    price_range_min: service.price_range_min || "",
    price_range_max: service.price_range_max || "",
    deposit_amount: service.deposit_amount || "",
    deposit_percent: service.deposit_percent || "",
    travel_fee: service.travel_fee || "",
    emergency_fee: service.emergency_fee || "",
    weekend_fee: service.weekend_fee || "",
    rush_fee: service.rush_fee || "",
    tax_behavior: service.tax_behavior || "excluded",
    materials_included: service.materials_included ?? true,
    expenses_allowed: service.expenses_allowed ?? false,
    currency: service.currency || "USD",
  });
  const setNum = (k) => (e) => setF({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) });
  const save = () => onSave({
    ...f,
    hourly_rate: f.hourly_rate === "" ? null : Number(f.hourly_rate),
    min_hours: f.min_hours === "" ? null : Number(f.min_hours),
    max_hours: f.max_hours === "" ? null : Number(f.max_hours),
    starting_price: f.starting_price === "" ? null : Number(f.starting_price),
    price_range_min: f.price_range_min === "" ? null : Number(f.price_range_min),
    price_range_max: f.price_range_max === "" ? null : Number(f.price_range_max),
    deposit_amount: f.deposit_amount === "" ? null : Number(f.deposit_amount),
    deposit_percent: f.deposit_percent === "" ? null : Number(f.deposit_percent),
    travel_fee: f.travel_fee === "" ? null : Number(f.travel_fee),
    emergency_fee: f.emergency_fee === "" ? null : Number(f.emergency_fee),
    weekend_fee: f.weekend_fee === "" ? null : Number(f.weekend_fee),
    rush_fee: f.rush_fee === "" ? null : Number(f.rush_fee),
  });
  return (
    <div className="max-w-2xl space-y-5" data-testid="wizard-pricing">
      <StepHeader title="Pricing" subtitle="How customers see and pay for this service." />
      <FieldRow cols={2}>
        <F label="Pricing model"><select value={f.pricing_model} onChange={(e) => setF({...f, pricing_model: e.target.value})} className="wz-input" data-testid="pricing-model">{PRICING_MODELS.map((m) => <option key={m}>{m}</option>)}</select></F>
        <F label="Currency"><input value={f.currency} onChange={(e) => setF({...f, currency: e.target.value})} className="wz-input" data-testid="pricing-currency" /></F>
      </FieldRow>
      <FieldRow cols={3}>
        <F label="Base price"><input type="number" value={f.base_price} onChange={setNum("base_price")} className="wz-input" data-testid="pricing-base" /></F>
        <F label="Hourly rate"><input type="number" value={f.hourly_rate} onChange={setNum("hourly_rate")} className="wz-input" data-testid="pricing-hourly" /></F>
        <F label="Starting price"><input type="number" value={f.starting_price} onChange={setNum("starting_price")} className="wz-input" data-testid="pricing-starting" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Price range min"><input type="number" value={f.price_range_min} onChange={setNum("price_range_min")} className="wz-input" data-testid="pricing-range-min" /></F>
        <F label="Price range max"><input type="number" value={f.price_range_max} onChange={setNum("price_range_max")} className="wz-input" data-testid="pricing-range-max" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Min hours"><input type="number" value={f.min_hours} onChange={setNum("min_hours")} className="wz-input" /></F>
        <F label="Max hours"><input type="number" value={f.max_hours} onChange={setNum("max_hours")} className="wz-input" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Deposit amount"><input type="number" value={f.deposit_amount} onChange={setNum("deposit_amount")} className="wz-input" /></F>
        <F label="Deposit %"><input type="number" value={f.deposit_percent} onChange={setNum("deposit_percent")} className="wz-input" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Travel fee"><input type="number" value={f.travel_fee} onChange={setNum("travel_fee")} className="wz-input" /></F>
        <F label="Emergency fee"><input type="number" value={f.emergency_fee} onChange={setNum("emergency_fee")} className="wz-input" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Weekend fee"><input type="number" value={f.weekend_fee} onChange={setNum("weekend_fee")} className="wz-input" /></F>
        <F label="Rush fee"><input type="number" value={f.rush_fee} onChange={setNum("rush_fee")} className="wz-input" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Tax behavior"><select value={f.tax_behavior} onChange={(e) => setF({...f, tax_behavior: e.target.value})} className="wz-input"><option value="excluded">Excluded</option><option value="included">Included</option></select></F>
        <div className="flex flex-col justify-center gap-2 pt-6">
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.materials_included} onChange={(e) => setF({...f, materials_included: e.target.checked})} className="h-4 w-4 rounded border-slate-300" /> Materials included</label>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.expenses_allowed} onChange={(e) => setF({...f, expenses_allowed: e.target.checked})} className="h-4 w-4 rounded border-slate-300" /> Expenses allowed</label>
        </div>
      </FieldRow>
      <SaveButton onClick={save} />
    </div>
  );
}

function ChildListStep({ service, rows, onChanged, path, id_prefix, defaultRow, columns, title, subtitle, addLabel, testidPrefix }) {
  const [editing, setEditing] = useState(null); // row data being edited
  const [saving, setSaving] = useState(false);

  const startNew = () => setEditing({ ...defaultRow });

  const saveRow = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const isNew = !editing.id;
      const url = isNew ? `/services/${service.id}/${path}` : `/services/${service.id}/${path}/${editing.id}`;
      const method = isNew ? "post" : "patch";
      await api[method](url, editing);
      toast.success(isNew ? "Added" : "Updated");
      setEditing(null);
      await onChanged();
    } catch (err) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (id) => {
    try {
      await api.delete(`/services/${service.id}/${path}/${id}`);
      toast.success("Removed");
      await onChanged();
    } catch (_) {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="space-y-5" data-testid={`wizard-${testidPrefix}`}>
      <StepHeader title={title} subtitle={subtitle} />
      <div className="grid gap-3">
        {rows.map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
            <GripVertical className="h-4 w-4 text-[#252A34]/40 shrink-0" />
            <div className="min-w-0 flex-1">
              {columns(r)}
            </div>
            <button onClick={() => setEditing(r)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs" data-testid={`${testidPrefix}-edit-${r.id}`}>Edit</button>
            <button onClick={() => removeRow(r.id)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-red-600 hover:border-red-300" data-testid={`${testidPrefix}-remove-${r.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-[#252A34]/70 bg-white text-sm">
            None yet — add your first below.
          </div>
        )}
      </div>

      {editing ? (
        <div className="rounded-2xl border border-[#165a71]/30 bg-[#165a71]/5 p-5 space-y-4" data-testid={`${testidPrefix}-editor`}>
          {Object.entries(defaultRow).map(([k, v]) => {
            if (typeof v === "boolean") {
              return (
                <label key={k} className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing[k]} onChange={(e) => setEditing({...editing, [k]: e.target.checked})} className="h-4 w-4 rounded border-slate-300" />
                  {k.replace(/_/g, " ")}
                </label>
              );
            }
            if (Array.isArray(v)) {
              return (
                <F key={k} label={k.replace(/_/g, " ")}>
                  <input value={(editing[k] || []).join(", ")} onChange={(e) => setEditing({...editing, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean)})} className="wz-input" data-testid={`${testidPrefix}-field-${k}`} placeholder="Comma-separated" />
                </F>
              );
            }
            if (typeof v === "number" && !["question_type"].includes(k)) {
              return <F key={k} label={k.replace(/_/g, " ")}><input type="number" value={editing[k] ?? ""} onChange={(e) => setEditing({...editing, [k]: e.target.value === "" ? null : Number(e.target.value)})} className="wz-input" data-testid={`${testidPrefix}-field-${k}`} /></F>;
            }
            if (k === "question_type") {
              return <F key={k} label={k.replace(/_/g, " ")}><select value={editing[k] || v} onChange={(e) => setEditing({...editing, [k]: e.target.value})} className="wz-input" data-testid={`${testidPrefix}-field-${k}`}>{QUESTION_TYPES.map((qt) => <option key={qt}>{qt}</option>)}</select></F>;
            }
            if (k === "tier") {
              return <F key={k} label={k}><select value={editing[k] || v} onChange={(e) => setEditing({...editing, [k]: e.target.value})} className="wz-input" data-testid={`${testidPrefix}-field-${k}`}><option value="basic">Basic</option><option value="standard">Standard</option><option value="premium">Premium</option></select></F>;
            }
            return <F key={k} label={k.replace(/_/g, " ")}><input value={editing[k] ?? ""} onChange={(e) => setEditing({...editing, [k]: e.target.value})} className="wz-input" data-testid={`${testidPrefix}-field-${k}`} /></F>;
          })}
          <div className="flex items-center gap-3">
            <button onClick={saveRow} disabled={saving} data-testid={`${testidPrefix}-save`} className="rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium hover:bg-[#11485a] disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setEditing(null)} data-testid={`${testidPrefix}-cancel`} className="text-sm text-[#252A34]/70 hover:text-[#252A34]">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={startNew} data-testid={`${testidPrefix}-add`} className="rounded-full border border-dashed border-slate-300 hover:border-[#165a71]/40 hover:text-[#165a71] px-5 py-2 text-sm font-medium inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> {addLabel}
        </button>
      )}
    </div>
  );
}

function PackagesStep(props) {
  return (
    <ChildListStep
      {...props}
      path="packages"
      id_prefix="pkg"
      title="Packages"
      subtitle="Offer up to 3 tiers (Basic / Standard / Premium). Skip for hourly or quote-only services."
      testidPrefix="pkg"
      addLabel="Add package"
      defaultRow={{ tier: "basic", name: "Basic", short_description: "", full_description: "", price: 0, delivery_time_days: 0, appointment_duration_minutes: 60, included_quantity: 1, revisions: 1, features: [], deliverables: [], materials: [], support_period_days: 0, is_active: true, sort_order: 0 }}
      columns={(r) => (
        <>
          <div className="font-medium text-[#071b22] capitalize">{r.name} — ${r.price}</div>
          <div className="text-xs text-[#252A34]/60">{r.short_description}</div>
        </>
      )}
    />
  );
}

function AddonsStep(props) {
  return (
    <ChildListStep
      {...props}
      path="addons"
      id_prefix="add"
      title="Add-ons"
      subtitle="Optional upgrades customers can pick at checkout."
      testidPrefix="add"
      addLabel="Add add-on"
      defaultRow={{ name: "", description: "", price: 0, additional_time_minutes: 0, additional_delivery_days: 0, max_quantity: 1, required: false, sort_order: 0 }}
      columns={(r) => (
        <>
          <div className="font-medium text-[#071b22]">{r.name}</div>
          <div className="text-xs text-[#252A34]/60">+${r.price}</div>
        </>
      )}
    />
  );
}

function RequirementsStep(props) {
  return (
    <ChildListStep
      {...props}
      path="requirements"
      id_prefix="req"
      title="Requirements"
      subtitle="What the customer must provide before you can start. Reordered top to bottom."
      testidPrefix="req"
      addLabel="Add requirement"
      defaultRow={{ question: "", question_type: "written", options: [], instructions: "", required: false, sort_order: 0 }}
      columns={(r) => (
        <>
          <div className="font-medium text-[#071b22]">{r.question}</div>
          <div className="text-xs text-[#252A34]/60">{r.question_type}{r.required && " · required"}</div>
        </>
      )}
    />
  );
}

function FaqsStep(props) {
  return (
    <ChildListStep
      {...props}
      path="faqs"
      id_prefix="faq"
      title="FAQs"
      subtitle="Answer common questions before they're asked."
      testidPrefix="faq"
      addLabel="Add FAQ"
      defaultRow={{ question: "", answer: "", sort_order: 0 }}
      columns={(r) => (
        <>
          <div className="font-medium text-[#071b22]">{r.question}</div>
          <div className="text-xs text-[#252A34]/60 line-clamp-1">{r.answer}</div>
        </>
      )}
    />
  );
}

function LocalDetailsStep({ service, onSave }) {
  const [f, setF] = useState({
    service_address_required: service.service_address_required ?? true,
    service_radius_miles: service.service_radius_miles ?? 25,
    custom_service_area: service.custom_service_area || "",
    travel_fee_rules: service.travel_fee_rules || "",
    appointment_duration_minutes: service.appointment_duration_minutes ?? 60,
    min_lead_time_hours: service.min_lead_time_hours ?? 24,
    same_day_availability: !!service.same_day_availability,
    emergency_availability: !!service.emergency_availability,
    parking_info: service.parking_info || "",
    equipment_requirements: service.equipment_requirements || "",
    materials_provided_by_customer: service.materials_provided_by_customer || "",
    materials_provided_by_provider: service.materials_provided_by_provider || "",
    property_access_instructions: service.property_access_instructions || "",
  });
  return (
    <div className="max-w-2xl space-y-5" data-testid="wizard-local">
      <StepHeader title="Local service details" subtitle="Where and how you deliver on-site." />
      <FieldRow cols={2}>
        <F label="Service radius (miles)"><input type="number" value={f.service_radius_miles} onChange={(e) => setF({...f, service_radius_miles: Number(e.target.value)})} className="wz-input" data-testid="local-radius" /></F>
        <F label="Appointment duration (min)"><input type="number" value={f.appointment_duration_minutes} onChange={(e) => setF({...f, appointment_duration_minutes: Number(e.target.value)})} className="wz-input" data-testid="local-duration" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Min lead time (hrs)"><input type="number" value={f.min_lead_time_hours} onChange={(e) => setF({...f, min_lead_time_hours: Number(e.target.value)})} className="wz-input" data-testid="local-lead" /></F>
        <F label="Custom service area"><input value={f.custom_service_area} onChange={(e) => setF({...f, custom_service_area: e.target.value})} className="wz-input" placeholder="e.g. Only Pensacola + Gulf Breeze" data-testid="local-custom-area" /></F>
      </FieldRow>
      <FieldRow><F label="Travel fee rules"><textarea rows={2} value={f.travel_fee_rules} onChange={(e) => setF({...f, travel_fee_rules: e.target.value})} className="wz-input" data-testid="local-travel-rules" /></F></FieldRow>
      <FieldRow><F label="Parking info"><input value={f.parking_info} onChange={(e) => setF({...f, parking_info: e.target.value})} className="wz-input" data-testid="local-parking" /></F></FieldRow>
      <FieldRow><F label="Equipment requirements"><input value={f.equipment_requirements} onChange={(e) => setF({...f, equipment_requirements: e.target.value})} className="wz-input" data-testid="local-equipment" /></F></FieldRow>
      <FieldRow cols={2}>
        <F label="Materials — customer provides"><textarea rows={2} value={f.materials_provided_by_customer} onChange={(e) => setF({...f, materials_provided_by_customer: e.target.value})} className="wz-input" data-testid="local-mats-customer" /></F>
        <F label="Materials — provider brings"><textarea rows={2} value={f.materials_provided_by_provider} onChange={(e) => setF({...f, materials_provided_by_provider: e.target.value})} className="wz-input" data-testid="local-mats-provider" /></F>
      </FieldRow>
      <FieldRow><F label="Property-access instructions"><textarea rows={2} value={f.property_access_instructions} onChange={(e) => setF({...f, property_access_instructions: e.target.value})} className="wz-input" data-testid="local-access" /></F></FieldRow>
      <div className="grid grid-cols-2 gap-3">
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.service_address_required} onChange={(e) => setF({...f, service_address_required: e.target.checked})} className="h-4 w-4 rounded border-slate-300" /> Service address required</label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.same_day_availability} onChange={(e) => setF({...f, same_day_availability: e.target.checked})} className="h-4 w-4 rounded border-slate-300" /> Same-day availability</label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.emergency_availability} onChange={(e) => setF({...f, emergency_availability: e.target.checked})} className="h-4 w-4 rounded border-slate-300" /> Emergency availability</label>
      </div>
      <SaveButton onClick={() => onSave(f)} />
    </div>
  );
}

function DigitalDetailsStep({ service, onSave }) {
  const [f, setF] = useState({
    delivery_format: service.delivery_format || "",
    source_files_included: !!service.source_files_included,
    commercial_use_rights: service.commercial_use_rights ?? true,
    revision_limit: service.revision_limit ?? 2,
    delivery_deadline_days: service.delivery_deadline_days ?? 7,
    rush_delivery_available: !!service.rush_delivery_available,
    file_types: (service.file_types || []).join(", "),
    software_used: (service.software_used || []).join(", "),
    customer_requirements: service.customer_requirements || "",
    portfolio_examples: (service.portfolio_examples || []).join(", "),
  });
  const save = () => onSave({
    ...f,
    file_types: f.file_types.split(",").map((s) => s.trim()).filter(Boolean),
    software_used: f.software_used.split(",").map((s) => s.trim()).filter(Boolean),
    portfolio_examples: f.portfolio_examples.split(",").map((s) => s.trim()).filter(Boolean),
  });
  return (
    <div className="max-w-2xl space-y-5" data-testid="wizard-digital">
      <StepHeader title="Digital delivery details" subtitle="How the file / service arrives with the customer." />
      <FieldRow cols={2}>
        <F label="Delivery format"><input value={f.delivery_format} onChange={(e) => setF({...f, delivery_format: e.target.value})} className="wz-input" placeholder="e.g. Figma + PDFs" data-testid="digital-format" /></F>
        <F label="Delivery deadline (days)"><input type="number" value={f.delivery_deadline_days} onChange={(e) => setF({...f, delivery_deadline_days: Number(e.target.value)})} className="wz-input" data-testid="digital-deadline" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Revision limit"><input type="number" value={f.revision_limit} onChange={(e) => setF({...f, revision_limit: Number(e.target.value)})} className="wz-input" data-testid="digital-revisions" /></F>
        <F label="File types"><input value={f.file_types} onChange={(e) => setF({...f, file_types: e.target.value})} className="wz-input" placeholder="AI, PDF, PSD" data-testid="digital-file-types" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Software used"><input value={f.software_used} onChange={(e) => setF({...f, software_used: e.target.value})} className="wz-input" placeholder="Figma, Illustrator" data-testid="digital-software" /></F>
        <F label="Portfolio examples (URLs)"><input value={f.portfolio_examples} onChange={(e) => setF({...f, portfolio_examples: e.target.value})} className="wz-input" data-testid="digital-portfolio" /></F>
      </FieldRow>
      <FieldRow><F label="Customer requirements"><textarea rows={3} value={f.customer_requirements} onChange={(e) => setF({...f, customer_requirements: e.target.value})} className="wz-input" data-testid="digital-customer-reqs" /></F></FieldRow>
      <div className="grid grid-cols-2 gap-3">
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.source_files_included} onChange={(e) => setF({...f, source_files_included: e.target.checked})} className="h-4 w-4 rounded border-slate-300" /> Source files included</label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.commercial_use_rights} onChange={(e) => setF({...f, commercial_use_rights: e.target.checked})} className="h-4 w-4 rounded border-slate-300" /> Commercial-use rights</label>
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.rush_delivery_available} onChange={(e) => setF({...f, rush_delivery_available: e.target.checked})} className="h-4 w-4 rounded border-slate-300" /> Rush delivery available</label>
      </div>
      <SaveButton onClick={save} />
    </div>
  );
}

function AvailabilityStep({ service, onSave }) {
  const [schedule, setSchedule] = useState(service.weekly_schedule || {});
  const [interval, setInterval] = useState(service.appointment_interval_minutes || 60);
  const [lead, setLead] = useState(service.lead_time_hours ?? 24);
  const [future, setFuture] = useState(service.max_future_days || 60);
  const [instant, setInstant] = useState(!!service.instant_accept);
  const [maxPerDay, setMaxPerDay] = useState(service.max_bookings_per_day || 3);

  useEffect(() => {
    if (!service.weekly_schedule || Object.keys(service.weekly_schedule).length === 0) {
      const init = {};
      DAYS.forEach((d) => { init[d] = { open: d !== "sunday", slots: d !== "sunday" ? [{ start: "09:00", end: "17:00" }] : [] }; });
      setSchedule(init);
    }
  }, [service.id]);

  const toggleDay = (day) => {
    const cur = schedule[day] || { open: false, slots: [] };
    setSchedule({ ...schedule, [day]: { ...cur, open: !cur.open, slots: !cur.open && cur.slots.length === 0 ? [{ start: "09:00", end: "17:00" }] : cur.slots } });
  };
  const updateSlot = (day, i, key, val) => {
    const cur = schedule[day];
    const slots = [...cur.slots];
    slots[i] = { ...slots[i], [key]: val };
    setSchedule({ ...schedule, [day]: { ...cur, slots } });
  };
  const addSlot = (day) => {
    const cur = schedule[day] || { open: true, slots: [] };
    setSchedule({ ...schedule, [day]: { ...cur, slots: [...cur.slots, { start: "09:00", end: "12:00" }] } });
  };
  const removeSlot = (day, i) => {
    const cur = schedule[day];
    setSchedule({ ...schedule, [day]: { ...cur, slots: cur.slots.filter((_, idx) => idx !== i) } });
  };

  const save = () => onSave({
    weekly_schedule: schedule,
    appointment_interval_minutes: Number(interval),
    lead_time_hours: Number(lead),
    max_future_days: Number(future),
    instant_accept: !!instant,
    max_bookings_per_day: Number(maxPerDay) || null,
  });

  return (
    <div className="space-y-5" data-testid="wizard-availability">
      <StepHeader title="Availability" subtitle="Weekly schedule + booking rules. Special dates & blocked dates coming next iteration." />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <F label="Interval (min)"><input type="number" value={interval} onChange={(e) => setInterval(e.target.value)} className="wz-input" data-testid="avail-interval" /></F>
        <F label="Lead time (hrs)"><input type="number" value={lead} onChange={(e) => setLead(e.target.value)} className="wz-input" data-testid="avail-lead" /></F>
        <F label="Max future (days)"><input type="number" value={future} onChange={(e) => setFuture(e.target.value)} className="wz-input" data-testid="avail-future" /></F>
        <F label="Max bookings/day"><input type="number" value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.value)} className="wz-input" data-testid="avail-max" /></F>
      </div>
      <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={instant} onChange={(e) => setInstant(e.target.checked)} data-testid="avail-instant" className="h-4 w-4 rounded border-slate-300" /> Instant accept</label>

      <div className="rounded-2xl border border-slate-200 bg-white divide-y" data-testid="weekly-schedule">
        {DAYS.map((d) => (
          <div key={d} className="p-4 flex items-start gap-4">
            <label className="inline-flex items-center gap-2 w-36 shrink-0">
              <input type="checkbox" checked={!!schedule[d]?.open} onChange={() => toggleDay(d)} data-testid={`day-${d}-toggle`} className="h-4 w-4 rounded border-slate-300" />
              <span className="capitalize font-medium text-[#071b22]">{d}</span>
            </label>
            <div className="flex-1 space-y-2">
              {(schedule[d]?.slots || []).map((slot, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="time" value={slot.start} onChange={(e) => updateSlot(d, i, "start", e.target.value)} className="wz-input max-w-[120px]" data-testid={`day-${d}-slot-${i}-start`} />
                  <span className="text-[#252A34]/50">–</span>
                  <input type="time" value={slot.end} onChange={(e) => updateSlot(d, i, "end", e.target.value)} className="wz-input max-w-[120px]" data-testid={`day-${d}-slot-${i}-end`} />
                  <button onClick={() => removeSlot(d, i)} className="text-red-600 text-xs" data-testid={`day-${d}-slot-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {schedule[d]?.open && (
                <button onClick={() => addSlot(d)} data-testid={`day-${d}-add-slot`} className="text-xs text-[#165a71] font-medium hover:underline inline-flex items-center gap-1">
                  <Plus className="h-3 w-3" /> Add slot
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <SaveButton onClick={save} />
    </div>
  );
}

function MediaStep({ service, onSave }) {
  const [featured, setFeatured] = useState(service.featured_image || "");
  const [gallery, setGallery] = useState((service.gallery || []).map((g) => g.url).join("\n"));
  const [video, setVideo] = useState(service.video_url || "");
  const [pdf, setPdf] = useState(service.pdf_url || "");

  const save = () => onSave({
    featured_image: featured || null,
    gallery: gallery.split("\n").map((u) => u.trim()).filter(Boolean).map((url) => ({ url, caption: "", alt: "" })),
    video_url: video || null,
    pdf_url: pdf || null,
  });

  return (
    <div className="max-w-2xl space-y-5" data-testid="wizard-media">
      <StepHeader title="Media" subtitle="Featured image + gallery, video, and any downloadable PDF." />
      <FieldRow><F label="Featured image URL"><input value={featured} onChange={(e) => setFeatured(e.target.value)} className="wz-input" data-testid="media-featured" /></F></FieldRow>
      <FieldRow><F label="Gallery (one URL per line)"><textarea rows={6} value={gallery} onChange={(e) => setGallery(e.target.value)} className="wz-input" data-testid="media-gallery" /></F></FieldRow>
      <FieldRow cols={2}>
        <F label="Video URL"><input value={video} onChange={(e) => setVideo(e.target.value)} className="wz-input" data-testid="media-video" /></F>
        <F label="PDF URL"><input value={pdf} onChange={(e) => setPdf(e.target.value)} className="wz-input" data-testid="media-pdf" /></F>
      </FieldRow>
      <SaveButton onClick={save} />
    </div>
  );
}

function PoliciesStep({ service, onSave }) {
  const [f, setF] = useState({
    cancellation_policy: service.cancellation_policy || "",
    rescheduling_policy: service.rescheduling_policy || "",
    refund_policy: service.refund_policy || "",
    revision_policy: service.revision_policy || "",
    late_arrival_policy: service.late_arrival_policy || "",
    no_show_policy: service.no_show_policy || "",
    weather_policy: service.weather_policy || "",
    customer_responsibilities: service.customer_responsibilities || "",
    provider_responsibilities: service.provider_responsibilities || "",
  });
  return (
    <div className="max-w-2xl space-y-5" data-testid="wizard-policies">
      <StepHeader title="Policies" subtitle="Clear rules save headaches later." />
      {Object.entries(f).map(([k, v]) => (
        <F key={k} label={k.replace(/_/g, " ")}>
          <textarea rows={2} value={v} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="wz-input" data-testid={`policy-${k}`} />
        </F>
      ))}
      <SaveButton onClick={() => onSave(f)} />
    </div>
  );
}

function SeoStep({ service, onSave }) {
  const [f, setF] = useState({
    seo_title: service.seo_title || "",
    seo_description: service.seo_description || "",
    seo_keywords: (service.seo_keywords || []).join(", "),
    social_image: service.social_image || "",
    canonical_url: service.canonical_url || "",
    index_page: service.index_page ?? true,
    custom_slug: service.custom_slug || "",
  });
  const save = () => onSave({
    ...f,
    seo_keywords: f.seo_keywords.split(",").map((s) => s.trim()).filter(Boolean),
  });
  return (
    <div className="max-w-2xl space-y-5" data-testid="wizard-seo">
      <StepHeader title="SEO" subtitle="How search engines and social share cards see this service." />
      <FieldRow><F label="SEO title"><input value={f.seo_title} onChange={(e) => setF({...f, seo_title: e.target.value})} className="wz-input" data-testid="seo-title" /></F></FieldRow>
      <FieldRow><F label="Meta description"><textarea rows={2} value={f.seo_description} onChange={(e) => setF({...f, seo_description: e.target.value})} className="wz-input" data-testid="seo-description" /></F></FieldRow>
      <FieldRow><F label="Keywords (comma-sep)"><input value={f.seo_keywords} onChange={(e) => setF({...f, seo_keywords: e.target.value})} className="wz-input" data-testid="seo-keywords" /></F></FieldRow>
      <FieldRow cols={2}>
        <F label="Social image URL"><input value={f.social_image} onChange={(e) => setF({...f, social_image: e.target.value})} className="wz-input" data-testid="seo-social" /></F>
        <F label="Canonical URL"><input value={f.canonical_url} onChange={(e) => setF({...f, canonical_url: e.target.value})} className="wz-input" data-testid="seo-canonical" /></F>
      </FieldRow>
      <FieldRow cols={2}>
        <F label="Custom slug"><input value={f.custom_slug} onChange={(e) => setF({...f, custom_slug: e.target.value})} className="wz-input" placeholder="my-service" data-testid="seo-slug" /></F>
        <label className="inline-flex items-center gap-2 text-sm pt-7"><input type="checkbox" checked={f.index_page} onChange={(e) => setF({...f, index_page: e.target.checked})} className="h-4 w-4 rounded border-slate-300" data-testid="seo-index" /> Allow search engines to index</label>
      </FieldRow>
      <SaveButton onClick={save} />
    </div>
  );
}

function ReviewStep({ service, packages, addons, requirements, faqs, onSubmit }) {
  return (
    <div className="space-y-6" data-testid="wizard-review">
      <StepHeader title="Review & submit" subtitle="A quick check — you can keep editing after submitting for review." />
      <div className="grid gap-3 md:grid-cols-2">
        <ReviewCard label="Basics">
          <div><b>{service.title}</b></div>
          <div className="text-xs text-[#252A34]/60">{service.short_description}</div>
          <div className="mt-2 text-xs">{service.kind} · {service.service_type?.replace(/_/g, " ")}</div>
        </ReviewCard>
        <ReviewCard label="Pricing">
          <div className="text-xs">{service.pricing_model}</div>
          <div className="mt-1">Base ${service.base_price} {service.hourly_rate ? ` · $${service.hourly_rate}/hr` : ""}</div>
        </ReviewCard>
        <ReviewCard label="Packages"><div>{packages.length} package(s)</div></ReviewCard>
        <ReviewCard label="Add-ons"><div>{addons.length} add-on(s)</div></ReviewCard>
        <ReviewCard label="Requirements"><div>{requirements.length} requirement(s)</div></ReviewCard>
        <ReviewCard label="FAQs"><div>{faqs.length} question(s)</div></ReviewCard>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-sm text-[#252A34]/80">
          When you submit, GCW will review your listing. Currently auto-approve is <b>off</b>, so it'll go to <i>pending_review</i> and become <i>published</i> after approval.
        </div>
      </div>
    </div>
  );
}

const ReviewCard = ({ label, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="text-xs uppercase tracking-wider text-[#252A34]/60 font-medium">{label}</div>
    <div className="mt-2 text-sm text-[#071b22]">{children}</div>
  </div>
);

const StepHeader = ({ title, subtitle }) => (
  <div>
    <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#071b22]">{title}</h2>
    {subtitle && <p className="text-sm text-[#252A34]/70 mt-1 max-w-2xl">{subtitle}</p>}
  </div>
);
const FieldRow = ({ children, cols = 1 }) => (
  <div className={`grid gap-4 grid-cols-1 ${cols === 2 ? "md:grid-cols-2" : cols === 3 ? "md:grid-cols-3" : ""}`}>{children}</div>
);
const F = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-[#252A34]/80 uppercase tracking-wider">{label}</span>
    {children}
  </label>
);
const SaveButton = ({ onClick }) => (
  <div className="pt-2">
    <button onClick={onClick} data-testid="wizard-save-step" className="rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#11485a] transition-colors inline-flex items-center gap-2">
      Save section <Check className="h-4 w-4" />
    </button>
  </div>
);
